import { STATUSES } from "../content/statuses";
import { afterStatus } from "./passives";
import { canAct, hasStatus } from "./query";
import type { StatusId, StatusInst, StatusPolarity, Unit, World } from "./types";
import { pushEvent } from "./combat";

export function applyStatus(
  world: World,
  target: Unit,
  id: StatusId,
  duration: number,
  source: Unit | null,
  opts: { copy?: boolean; silent?: boolean } = {},
): boolean {
  if (!target || duration <= 0) return false;
  if (target.kind === "corpse" && target.dead && id !== "bleeding" && id !== "curse") {
    return false;
  }
  const def = STATUSES[id];
  if (!def) return false;
  const existing = target.statuses.find((s) => s.id === id);
  if (existing) {
    if (def.stack === "ignore") return false;
    existing.remaining = Math.max(existing.remaining, duration);
    existing.duration = Math.max(existing.duration, duration);
    if (source) existing.sourceId = source.id;
    applyLock(world, target, id, existing.remaining);
    if (id === "wound") applyStatus(world, target, "bleeding", duration, source, { silent: true });
    return false;
  }
  const inst: StatusInst = {
    id,
    remaining: duration,
    duration,
    sourceId: source?.id ?? -1,
    copy: !!opts.copy,
    tickAcc: 0,
  };
  target.statuses.push(inst);
  applyLock(world, target, id, duration);
  if (id === "wound") applyStatus(world, target, "bleeding", duration, source, { silent: opts.silent });
  if (!opts.silent) afterStatus(world, target, id, source, inst);
  return true;
}

function applyLock(world: World, target: Unit, id: StatusId, duration: number): void {
  const def = STATUSES[id];
  if (def.tags.includes("lock")) {
    target.lockout = Math.max(target.lockout, duration);
    if (def.tags.includes("interrupt")) interruptCast(world, target);
  }
}

export function removeStatus(unit: Unit, id: StatusId): boolean {
  const n = unit.statuses.length;
  unit.statuses = unit.statuses.filter((s) => s.id !== id);
  return unit.statuses.length < n;
}

export function stripPolarity(unit: Unit, polarity: StatusPolarity): StatusId[] {
  const removed: StatusId[] = [];
  unit.statuses = unit.statuses.filter((s) => {
    if (STATUSES[s.id].polarity === polarity) {
      removed.push(s.id);
      return false;
    }
    return true;
  });
  return removed;
}

export function stripOneCondition(unit: Unit): StatusId | null {
  const idx = unit.statuses.findIndex((s) => STATUSES[s.id].polarity === "condition" && !STATUSES[s.id].tags.includes("mark"));
  if (idx < 0) return null;
  const id = unit.statuses[idx].id;
  unit.statuses.splice(idx, 1);
  return id;
}

export function interruptCast(world: World, unit: Unit): boolean {
  if (!unit.casting && !unit.charging) return false;
  unit.casting = null;
  if (unit.charging?.kind === "slam" || (unit.charging && unit.charging.speed === 0)) {
    unit.charging = null;
  } else if (unit.charging && unit.charging.kind !== "line") {
    unit.charging = null;
  }
  if (unit.charging && unit.charging.traveled === 0 && unit.charging.speed === 0) unit.charging = null;
  if (unit.casting === null && unit.charging && unit.charging.kind === "slam") unit.charging = null;
  unit.lockout = Math.max(unit.lockout, 0.2);
  pushEvent(world, {
    kind: "interrupt",
    x: unit.pos.x,
    y: unit.pos.y - 0.55,
    text: "INTERRUPTED",
    team: unit.team,
  });
  return true;
}

export function tickStatusClock(unit: Unit, dt: number): StatusInst[] {
  const due: StatusInst[] = [];
  const keep: StatusInst[] = [];
  for (const s of unit.statuses) {
    const def = STATUSES[s.id];
    if (def.tickEvery > 0) {
      s.tickAcc += dt;
      while (s.tickAcc >= def.tickEvery) {
        s.tickAcc -= def.tickEvery;
        due.push(s);
      }
    }
    s.remaining -= dt;
    if (s.remaining > 0) keep.push(s);
  }
  unit.statuses = keep;
  return due;
}

export { hasStatus, canAct };
